// Third party imports
import React, { useEffect, Suspense, useState } from "react";
import Cookies from "js-cookie";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// local imports
import Loader from "./components/Loader";
import ProtectedLayout from "./ProtectedLayout";
import VisitorIdHandler from "./VisitorIdHandler";
import DisableNumberScroll from "./DisableNumberScroll";
import { companyProfile } from "./store/reducers/profileSlice";
import RouteChangeTracker from "./components/RouteChangeTracker";
import { dashboard } from "./store/reducers/pdfeditor/dashboardSlice";
import {
  clearUploadedDocumentFromTab,
  toggleFullView,
} from "./store/reducers/uploadDocumentSlice";

// Lazy imports
const Login = React.lazy(() => import("./pages/Login"));
const Signup = React.lazy(() => import("./pages/SignIn"));
const Wallet = React.lazy(() => import("./pages/Wallet"));
const Premium = React.lazy(() => import("./pages/Premium"));
const Signers = React.lazy(() => import("./pages/Signers"));
const StampPapers = React.lazy(() => import("./pages/StampPapers"));
const Workflow = React.lazy(() => import("./pages/Workflow"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Documents = React.lazy(() => import("./pages/Documents"));
const BulkEsign = React.lazy(() => import("./pages/BulkEsign"));
const MyProfile = React.lazy(() => import("./pages/MyProfile"));
const PdfEditor = React.lazy(() => import("./pages/pdfmapping"));
const MyDocuments = React.lazy(() => import("./pages/pdfEditor"));
const MakerChecker = React.lazy(() => import("./pages/MakerChecker"));
const APIIntegration = React.lazy(() => import("./pages/APIIntegration"));
const NewPasswrod = React.lazy(() => import("./pages/Login/newPassword"));
const RecoveryAccount = React.lazy(() => import("./pages/Login/recovery"));
const VerifySignupOTP = React.lazy(() => import("./pages/SignIn/SignupOtp"));
const SignupDetails = React.lazy(() => import("./pages/SignIn/SignupDetails"));
const ForgotPassword = React.lazy(() => import("./pages/Login/forgotPasswrod"));

// settings imports
const Settings = React.lazy(() => import("./pages/Settings"));
const EmailTemplates = React.lazy(
  () => import("./pages/Settings/EmailTemplates"),
);

const ChooseTemplate = React.lazy(
  () => import("./components/Esign/chooseTemplate"),
);
const LazyPdfViewer = React.lazy(
  () => import("./components/Documents/pdflazyview"),
);
const Archive = React.lazy(
  () => import("./pages/Archive")
);
// super admin imports
const Superdashboard = React.lazy(
  () => import("./pages/Superadmin/Superdashboard"),
);
const SuperadminLogin = React.lazy(
  () => import("./pages/Superadmin/SuperadminLogin"),
);

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { uploadedDocument } = useSelector((state) => state.uploadDocument);
  // The cookie is the session marker. Legacy localStorage tokens can outlive it.
  const [token, setToken] = useState(() => Cookies.get("token") || null);

  useEffect(() => {
    localStorage.removeItem("token");
    const checkToken = () => setToken(Cookies.get("token") || null);
    const interval = setInterval(checkToken, 1000);
    window.addEventListener("focus", checkToken);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", checkToken);
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    dispatch(companyProfile());
    dispatch(dashboard());
  }, [token, dispatch]);

  useEffect(() => {
    if (
      location.pathname !== "/esign/pdfeditor" &&
      location.pathname !== "/esign/new-esign" &&
      location.pathname !== "/esign/bulk-esign" &&
      uploadedDocument
    ) {
      dispatch(clearUploadedDocumentFromTab());
      dispatch(toggleFullView());
    }
  }, [location.pathname, uploadedDocument, dispatch]);

  return (
    <>
      <VisitorIdHandler />
      <RouteChangeTracker />
      <DisableNumberScroll />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route
            path="/signup"
            element={
              token ? <Navigate to="/esign/new-esign" replace /> : <Signup />
            }
          />
          <Route
            path="/verify-otp"
            element={
              token ? (
                <Navigate to="/esign/new-esign" replace />
              ) : localStorage.getItem("email") ? (
                <VerifySignupOTP />
              ) : (
                <Navigate to="/signup" />
              )
            }
          />
          <Route
            path="/sign-in-details"
            element={
              token ? (
                <Navigate to="/esign/new-esign" replace />
              ) : localStorage.getItem("email") ? (
                <SignupDetails />
              ) : (
                <Navigate to="/signup" />
              )
            }
          />
          <Route
            path="/login"
            element={
              token ? <Navigate to="/esign/new-esign" replace /> : <Login />
            }
          />
          <Route
            path="/forgot-password"
            element={
              token ? (
                <Navigate to="/esign/new-esign" replace />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route
            path="/new-password"
            element={
              token ? (
                <Navigate to="/esign/new-esign" replace />
              ) : (
                <NewPasswrod />
              )
            }
          />
          <Route
            path="/recovery-account"
            element={
              token ? (
                <Navigate to="/esign/new-esign" replace />
              ) : (
                <RecoveryAccount />
              )
            }
          />

        {/* super admin routes */}
        <Route path="/superlogin" element={<SuperadminLogin />} />
        <Route path="/superdashboard" element={<Superdashboard />} />

          <Route element={<ProtectedLayout token={token} />}>
            <Route path="/dashboard" element={<Dashboard />} />
            {/* <Route path="/dashboard" element={<Premium type="dashboard" />} /> */}
            <Route path="/lazypdf" element={<LazyPdfViewer />} />
            <Route path="/esign/new-esign" element={<MyDocuments />} />
            <Route path="/esign/choose-template" element={<ChooseTemplate />} />
            <Route
              path="/esign/pdfeditor"
              element={
                uploadedDocument?.data?.document_id &&
                uploadedDocument?.fullView ? (
                  <PdfEditor />
                ) : (
                  <Navigate to="/esign/new-esign" replace />
                )
              }
            />
            <Route path="/esign/stamp-paper" element={<StampPapers />} />
            <Route path="/esign/documents" element={<Documents />} />
            <Route path="/esign/bulk-esign" element={<BulkEsign />} />
            <Route path="/esign/signers" element={<Signers />} />
            <Route path="/esign/archive" element={<Archive />} />
            <Route path="/esign/trash" element={<Premium type="trash" />} />
            {/* <Route path="/workflow" element={<Premium type="workflow" />} /> */}
            <Route path="/workflow/workflow-list" element={<Workflow />} />
            <Route path="/workflow/records" element={<MakerChecker />} />
            <Route path="/workflow/wallet" element={<Wallet />} />

            {/* <Route path="/api-integration" element={<Premium type="api-integration" />} /> */}
            <Route path="/api-integration" element={<APIIntegration />} />
            <Route
              path="/our-products"
              element={<Premium type="our-products" />}
            />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/settings/email-templates"
              element={<EmailTemplates />}
            />
            <Route
              path="/my-profile"
              element={<MyProfile ActiveTab={"my-profile"} />}
            />
            <Route
              path="/change-password"
              element={<MyProfile ActiveTab={"change-password"} />}
            />
            <Route
              path="/credits"
              element={<MyProfile ActiveTab={"credits"} />}
            />
            <Route
              path="/help-support"
              element={<MyProfile ActiveTab={"help-support"} />}
            />
          </Route>
          <Route
            path="*"
            element={
              <Navigate to={token ? "/esign/new-esign" : "/signup"} replace />
            }
          />
        </Routes>
      </Suspense>
      <ToastContainer
        position="bottom-right"
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        autoClose={3000}
      />
    </>
  );
}

export default App;
